/**
 * Timesheets with the gate and analysis — ADR-001 §8.6, §11.6, guide §2, §5.5, §6.6, ADR-005 (working
 * days come from the published holiday calendar), operator decisions 8, 9, 18 and the acting-as rule.
 *
 * Visibility is enforced, not styled: a member reads only their own days (RLS); `GET /timesheets` is 403
 * for ops_member; no route shows a member a colleague's total. The AD and founder are not gated.
 * While acting as someone else, filing that person's timesheet is refused (a manager edits in their own
 * name instead, via PATCH /timesheets/{user_id}/{date}).
 */
import { z } from 'zod';
import { IsoDate, PageQuery, Timestamp, Uuid, Version, page } from './common.js';
import { UserRef } from './users.js';
import { ClientName } from './requests.js';
/**
 * `in` filed on time (by the on-time cut-off, Settings `timesheet.on_time_by`, default 18:30 IST, that day), `late` filed after, `missing` not filed on a working day,
 * `open` today and not filed yet, `not_expected` a weekend, holiday or elected optional holiday.
 * Only `missing` gates (ADR §8.6).
 */
export const TimesheetStatus = z.enum(['in', 'late', 'missing', 'open', 'not_expected']).meta({ id: 'TimesheetStatus' });
export const TimesheetEntry = z
    .object({
    id: Uuid,
    /** `null` = internal work (then `work_type` says what). */
    client: ClientName.nullable(),
    work_type: z.string().nullable(),
    hours: z.number().min(0).max(24),
    note: z.string().nullable(),
})
    .meta({ id: 'TimesheetEntry' });
export const TimesheetDay = z
    .object({
    user: UserRef,
    date: IsoDate,
    /** From the published calendar and the working-day setting (default 7.5; 0 on holidays). */
    expected_hours: z.number().min(0).max(24),
    /** `null` until filed. */
    hours_filed: z.number().min(0).max(24).nullable(),
    entries: z.array(TimesheetEntry),
    status: TimesheetStatus,
    /** The on-time cut-off for this day (Settings `timesheet.on_time_by`, default 18:30 IST). */
    on_time_by: Timestamp,
    filed_at: Timestamp.nullable(),
    /** Set when a manager filed or edited it on the person's behalf ("filed by {manager}"). */
    filed_by: UserRef.nullable(),
    /** A manager's later correction of a filed day (ADR §11.6) — filed_by stays whoever filed it. */
    edited_by: UserRef.nullable(),
    edited_at: Timestamp.nullable(),
    locked: z.boolean(),
    /** Planned minutes from the day plan, for comparison. */
    planned_minutes: z.number().int().nonnegative(),
    /** The member's own words on the day ("How was the day? What took too long, and what could be automated?"). */
    reflection: z.string().nullable(),
    version: Version.nullable(),
})
    .meta({ id: 'TimesheetDay' });
export const MyTimesheetQuery = z.object({ date: IsoDate.optional() });
export const TimesheetDateParams = z.object({ date: IsoDate });
const EntryIn = z
    .object({
    client_id: Uuid.optional(),
    work_type: z.string().trim().min(2).max(60).optional(),
    hours: z.number().min(0.25, 'Log at least a quarter of an hour').max(24),
    note: z.string().trim().max(300).optional(),
})
    .strict()
    .refine((e) => !!e.client_id !== !!e.work_type, { message: 'Pick a client, or say what internal work it was — one of the two' });
/**
 * File (or re-file) a day. Entries must add up to `hours_filed` (422 naming `entries`). 409 when the day is
 * locked. 403 while acting as someone else. A future date is 422. `reflection`: omitted keeps the stored one,
 * an empty string clears it.
 */
export const FileTimesheetBody = z
    .object({
    hours_filed: z.number().min(0).max(24),
    entries: z.array(EntryIn).max(40),
    reflection: z.string().trim().max(1000).optional(),
})
    .strict()
    .superRefine((b, ctx) => {
    const sum = Math.round(b.entries.reduce((a, e) => a + e.hours, 0) * 100) / 100;
    if (Math.abs(sum - b.hours_filed) > 0.01) {
        ctx.addIssue({ code: 'custom', path: ['entries'], message: `The entries add up to ${sum} h but you filed ${b.hours_filed} h. Make them match.` });
    }
});
// ---------------------------------------------------------------- the gate
export const GateItem = z
    .object({
    task_id: Uuid,
    title: z.string(),
    client: ClientName.nullable(),
    planned_minutes: z.number().int().nonnegative(),
    /** An answer already given on the day, if any. */
    state: z.enum(['done', 'part', 'no']).nullable(),
})
    .meta({ id: 'GateItem' });
/** GET /me/timesheet/gate — ops_member only is ever blocked (the AD and founder are not gated). */
export const TimesheetGateResponse = z
    .object({
    blocked: z.boolean(),
    /** The user's own previous working day (optional-holiday elections differ per person, ADR-005 §4). */
    previous_working_day: IsoDate.nullable(),
    previous_status: TimesheetStatus.nullable(),
    items_to_answer: z.array(GateItem),
})
    .meta({ id: 'TimesheetGateResponse' });
/** "Fill {day}'s timesheet to open today" — files the missed day as `late` and lifts the gate. */
export const CatchUpBody = z
    .object({
    work_date: IsoDate,
    answers: z
        .array(z.object({
        task_id: Uuid,
        state: z.enum(['done', 'part', 'no']),
        actual_minutes: z.number().int().min(0).max(1440),
        note: z.string().trim().max(300).optional(),
    }).strict())
        .max(60),
    /** "Other work" not on the plan. */
    extras: z.array(z.object({ text: z.string().trim().min(2).max(200), minutes: z.number().int().min(1).max(1440), client_id: Uuid.optional() }).strict()).max(20),
    reflection: z.string().trim().max(1000).optional(),
})
    .strict()
    .refine((b) => b.answers.length + b.extras.length > 0, { message: 'Say what you worked on, or add the other work you did' })
    // The day's total becomes hours_filed (0–24): more than 24 hours in one day is a 422, never a 500 (S4-G-09).
    .refine((b) => b.answers.reduce((n, a) => n + a.actual_minutes, 0) + b.extras.reduce((n, e) => n + e.minutes, 0) <= 1440, {
    message: 'That adds up to more than 24 hours in one day. Check the minutes.',
    path: ['extras'],
});
export const CatchUpResponse = z.object({ unblocked: z.literal(true), day: TimesheetDay }).meta({ id: 'CatchUpResponse' });
// ---------------------------------------------------------------- manager views
/**
 * At most 92 days per request (a quarter), like GET /working-days. `from` and `to` come together or not at all:
 * with neither, the range is the `view` period containing today. One alone was filled in from today by the handler,
 * so the contract could not check the range the handler would refuse (release review t-94302203 (2)).
 */
const RANGE_RULE = 'Send with the other bound or neither (neither = the view period containing today). from <= to, at most 92 days.';
export const TimesheetsQuery = z
    .object({
    view: z.enum(['day', 'week', 'month']).default('week'),
    from: IsoDate.optional().meta({ description: `First day. ${RANGE_RULE}` }),
    to: IsoDate.optional().meta({ description: `Last day. ${RANGE_RULE}` }),
    user_id: Uuid.optional(),
})
    .refine((q) => !q.from === !q.to, { path: ['to'], message: 'Send from and to together, or neither' })
    .refine((q) => !q.from || !q.to || q.from <= q.to, { path: ['to'], message: 'to must be on or after from' })
    .refine((q) => !q.from || !q.to || (Date.parse(q.to) - Date.parse(q.from)) / 86_400_000 <= 92, { path: ['to'], message: 'At most 92 days at a time' });
export const TimesheetCell = z
    .object({
    date: IsoDate,
    expected_hours: z.number(),
    hours: z.number().nullable(),
    status: TimesheetStatus,
    /**
     * The day's planned hours: the day plan's minutes / 60, a manager's planned-minutes correction winning (the Week
     * grid's "{logged} / of {planned}"). `null` = no plan for that day and no correction (the grid leaves it blank).
     */
    planned_hours: z.number().nonnegative().nullable(),
})
    .meta({ id: 'TimesheetCell' });
export const TimesheetAnalysis = z
    .object({
    hours_logged: z.number(),
    /** max(0, expected − filed) per working day; a missing day counts its full expected hours. */
    hours_not_logged: z.number(),
    by_client: z.array(z.object({ client: ClientName, hours: z.number() })),
    internal_hours: z.number(),
})
    .meta({ id: 'TimesheetAnalysis' });
/**
 * One person's one day, for the Day view (`view=day`, `from == to`) — the prototype's day table: Plan confirmed /
 * Timesheet (status · filed time) / Logged / planned / Tasks done x of y / Other work / Their note / Nudged.
 */
export const TimesheetGridDay = z
    .object({
    /** When the person confirmed their day plan; `null` = not confirmed (or no plan). */
    plan_confirmed_at: Timestamp.nullable(),
    filed_at: Timestamp.nullable(),
    /** A manager who filed a missing day for them ("filed by {manager}"). */
    filed_by: UserRef.nullable(),
    /** A manager who later corrected the filed day. */
    edited_by: UserRef.nullable(),
    /** The plan's minutes (a manager's correction wins). */
    planned_minutes: z.number().int().nonnegative(),
    /** Planned tasks answered "done" (a manager's correction wins; then it may exceed `tasks_planned`). */
    tasks_done: z.number().int().nonnegative(),
    /** Tasks in the day plan. */
    tasks_planned: z.number().int().nonnegative(),
    /** Minutes of work outside the plan ("Other work"; a manager's correction wins). */
    other_minutes: z.number().int().nonnegative(),
    /** The person's own words on the day (their reflection). */
    note: z.string().nullable(),
    /** The latest timesheet nudge about this day: when, and how many times. */
    last_nudge: z.object({ at: Timestamp, count: z.number().int().positive() }).nullable(),
})
    .meta({ id: 'TimesheetGridDay' });
/**
 * GET /timesheets — managers. A grid of people against dates (coloured by hours against expected).
 * `analysis` is present only with `timesheet.analysis.read` (guide §2: the founder sees "everyone's, with
 * analysis and feedback"; the associate director "everyone's").
 */
export const TimesheetsResponse = z
    .object({
    view: z.enum(['day', 'week', 'month']),
    from: IsoDate,
    to: IsoDate,
    expected_per_day: z.number(),
    grid: z.array(z.object({
        user: UserRef,
        /**
         * The timesheet gate applies to this person (capability `timesheet.gated`): a missed day locks their next
         * day. False for a filer who is never gated (the Ops associate director, operator ruling 2026-10-16) —
         * their missed day is "not filed", with no lock. Optional only for an older server.
         */
        gated: z.boolean().optional(),
        cells: z.array(TimesheetCell),
        /**
         * Over the period. `planned_hours` / `tasks_done` / `tasks_planned` give "Plan against what happened"
         * (hours vs plan %, tasks done %); `late` / `missing` count days with that status.
         */
        totals: z.object({
            logged: z.number(),
            not_logged: z.number(),
            planned_hours: z.number().nonnegative(),
            tasks_done: z.number().int().nonnegative(),
            tasks_planned: z.number().int().nonnegative(),
            late: z.number().int().nonnegative(),
            missing: z.number().int().nonnegative(),
        }),
        /** Only for a single day (`from == to`): the day-table columns. */
        day: TimesheetGridDay.optional(),
    })),
    analysis: TimesheetAnalysis.optional(),
})
    .meta({ id: 'TimesheetsResponse' });
export const UserDateParams = z.object({ user_id: Uuid, date: IsoDate });
/** A manager edit, in the manager's own name. Editing a `missing` day files it `late`, "filed by {manager}". */
export const ManagerEditTimesheetBody = z
    .object({
    hours_filed: z.number().min(0).max(24),
    planned_minutes: z.number().int().min(0).max(1440).optional(),
    tasks_done: z.number().int().min(0).max(200).optional(),
    other_minutes: z.number().int().min(0).max(1440).optional(),
    note: z.string().trim().min(3, 'Say why you are editing it').max(500),
})
    .strict();
// ---------------------------------------------------------------- nudges
export const Nudge = z
    .object({
    id: Uuid,
    to: UserRef,
    by: UserRef,
    kind: z.enum(['timesheet', 'task']),
    /** Times nudged; a repeat nudge increments this rather than adding a row. */
    count: z.number().int().positive(),
    first_at: Timestamp,
    last_at: Timestamp,
})
    .meta({ id: 'Nudge' });
export const CreateNudgeBody = z
    .object({ to_user_id: Uuid, kind: z.enum(['timesheet', 'task']), task_id: Uuid.optional() })
    .strict()
    .refine((b) => b.kind !== 'task' || !!b.task_id, { path: ['task_id'], message: 'Pick the task to nudge about' });
// ---------------------------------------------------------------- manager feedback
/** Prototype FBCATS. */
export const FeedbackCategory = z
    .enum(['Template', 'Waiting', 'Meetings', 'Reports', 'Process', 'Training', 'Other'])
    .meta({ id: 'FeedbackCategory' });
/** Same shape as `ManagerFeedback` on the member's Today, plus who it is for. */
export const TimesheetFeedback = z
    .object({
    id: Uuid,
    user: UserRef,
    by: UserRef,
    /** e.g. "Week of 28 Sep". */
    period: z.string(),
    category: FeedbackCategory,
    body: z.string(),
    at: Timestamp,
})
    .meta({ id: 'TimesheetFeedback' });
export const FeedbackPage = page(TimesheetFeedback).meta({ id: 'FeedbackPage' });
export const ListFeedbackQuery = PageQuery.extend({ user_id: Uuid.optional(), period: z.string().max(60).optional() });
export const CreateFeedbackBody = z
    .object({
    user_id: Uuid,
    period: z.string().trim().min(3).max(60),
    category: FeedbackCategory,
    body: z.string().trim().min(10, 'Say how they could save time, in a sentence or two').max(1000),
})
    .strict();
export const FeedbackIdParams = z.object({ id: Uuid });
// ---------------------------------------------------------------- automation candidates
export const AutomationStatus = z.enum(['Idea', 'Planned', 'Building', 'Live']).meta({ id: 'AutomationStatus' });
/** Prototype SZ. */
export const AutomationSize = z.enum(['S', 'M', 'L']).meta({ id: 'AutomationSize' });
export const AUTOMATION_SIZE_LABEL = {
    S: 'Small, about a week',
    M: 'Medium, about a month',
    L: 'Large, a quarter',
};
/** CSV header, exactly the prototype's "Download as Excel" columns. */
export const AUTOMATION_CSV_COLUMNS = ['Task', 'How often', 'Hours a week', 'Done by', 'Idea to automate it', 'Size', 'Status', 'Added by'];
/**
 * A text cell for a CSV download or a copy pasted into a sheet: one that starts with = + - @, a tab or a carriage
 * return would be read by Excel / Google Sheets as a formula (CSV injection, sf-577ff216), so it gets a leading
 * apostrophe, which the sheet keeps as text and does not show. Only for text: numbers stay as they are.
 */
export const spreadsheetCell = (v) => (/^[=+\-@\t\r]/.test(v) ? `'${v}` : v);
/**
 * One cell of a tab-separated copy that is pasted into a sheet. The sheet splits a paste on tab and on line breaks,
 * so a tab, CR or LF inside a value would start a new cell whose first character spreadsheetCell never saw
 * ('ok\n=HYPERLINK(…)'). Each run of them becomes one space first, then the cell is guarded as above. Not for the
 * CSV download, which quotes its fields and keeps line breaks inside them.
 */
export const pastedCell = (v) => spreadsheetCell(v.replace(/[\t\r\n]+/g, ' '));
/** Rows of text as a tab-separated block for the clipboard: every cell through pastedCell. */
export const pastedSheet = (rows) => rows.map((r) => r.map(pastedCell).join('\t')).join('\n');
/**
 * Tasks that repeat and could be pushed to a sheet or script (ADR §8.6): non-client task titles grouped by
 * a normalised signature, 3+ occurrences in the period, hours summed from timesheets/day status.
 */
export const AutomationCandidate = z
    .object({
    id: Uuid,
    period: z.string(),
    title: z.string(),
    occurrences: z.number().int().positive(),
    hours_per_week: z.number().nonnegative(),
    how_often: z.enum(['Daily', 'Weekly', 'Fortnightly', 'Monthly']),
    done_by: z.array(UserRef),
    /** Idea to automate it. */
    fix: z.string().nullable(),
    size: AutomationSize.nullable(),
    /** `null` until a manager adds it to the automation sheet. */
    status: AutomationStatus.nullable(),
    in_sheet: z.boolean(),
    added_by: UserRef.nullable(),
})
    .meta({ id: 'AutomationCandidate' });
export const AutomationPage = page(AutomationCandidate).meta({ id: 'AutomationPage' });
/** `period` is `YYYY-MM`; default the current month. */
export const AutomationQuery = PageQuery.extend({ period: z.string().regex(/^\d{4}-\d{2}$/, 'Use YYYY-MM').optional(), in_sheet: z.enum(['true', 'false']).transform((s) => s === 'true').optional() });
export const AutomationCsvQuery = z.object({ period: z.string().regex(/^\d{4}-\d{2}$/).optional() });
export const AutomationIdParams = z.object({ id: Uuid });
export const UpdateAutomationBody = z
    .object({
    status: AutomationStatus.nullable().optional(),
    in_sheet: z.boolean().optional(),
    how: z.enum(['Daily', 'Weekly', 'Fortnightly', 'Monthly']).optional(),
    fix: z.string().trim().min(3).max(500).optional(),
    size: AutomationSize.optional(),
})
    .strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one field' });
/**
 * "Add one of your own" (prototype autoAddOwn): a manager adds a task straight to the automation sheet. It is
 * created in the sheet with status Idea, nobody in "Done by", and counts as 1 occurrence. Defaults follow the
 * prototype: how often Weekly, size M, fix "To be worked out" (shown when `fix` is null). `period` defaults to
 * the current month.
 */
export const CreateAutomationBody = z
    .object({
    title: z.string().trim().min(3, 'Describe the task').max(200),
    /** Shown to one decimal. */
    hours_per_week: z.number().min(0).max(168),
    how_often: z.enum(['Daily', 'Weekly', 'Fortnightly', 'Monthly']).default('Weekly'),
    fix: z.string().trim().min(3).max(500).optional(),
    size: AutomationSize.default('M'),
    period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use YYYY-MM').optional(),
})
    .strict();
// ---------------------------------------------------------------- the working-day setting
/** Settings → working day: the expected hours of a full working day (decision 9; default 7.5; half days are half). */
export const WorkingDayBody = z
    .object({ hours: z.number().min(1).max(12).multipleOf(0.25, 'Use quarter hours, for example 7.5') })
    .strict();
export const WorkingDaySetting = z
    .object({
    working_day_hours: z.number().positive().max(24),
    /** The settings version (one number across all settings). Send it back as If-Match on the PATCH. */
    config_version: z.number().int().positive(),
    updated_at: Timestamp.nullable(),
    /** `null` for the seeded default. */
    updated_by: UserRef.nullable(),
})
    .meta({ id: 'WorkingDaySetting' });
//# sourceMappingURL=timesheets.js.map